import { Card, CardActions, CardContent, Grid, Skeleton, useTheme } from '@mui/material';
import React from 'react';
import { CerebroSecondaryButton, CerebroText } from '../../../shared/components';
import { Deal, TermSheet, TermSheetFacility, TermSheetRevision } from '../../../shared/models';
import { EnumUtils, NumberUtils } from '../../../shared/utils';
import { AddFacilityDrawer } from './AddFacilityDrawer';
import { EditFacilityDrawer } from './EditFacilityDrawer';
import { FacilityCollateralDrawer } from './FacilityCollateralDrawer';
import { FacilityDeleteDialog } from './FacilityDeleteDialog';
import { FacilityFeeDrawer } from './FacilityFeeDrawer';
import { isFactorPO, isLeaseFacilityType } from '../../../shared/utils/entity';
import { useDataRoomQuery, useDealQuery } from '../../../shared/hooks';
import { PainPointConfirmationDialog } from './PainPointConfirmationDialog';
import { TermSheetFacilityActionButton } from './TermSheetFacilityActionButton';

interface FacilityListProps {
	dataRoomGuid: string | undefined;
	termSheetGuid: string | undefined;
	revisionGuid: string | undefined;
	termSheet: TermSheet;
	revision: TermSheetRevision;
	isLoading: boolean;
}

export const FacilityList: React.FC<FacilityListProps> = ({
	dataRoomGuid,
	termSheetGuid,
	revisionGuid,
	termSheet,
	revision,
	isLoading,
}) => {
	const [facilityBeingAdded, setFacilityBeingAdded] = React.useState<boolean>(false);
	const [facilityBeingDeleted, setFacilityBeingDeleted] = React.useState<TermSheetFacility | null>(null);
	const [facilityBeingEdited, setFacilityBeingEdited] = React.useState<TermSheetFacility | null>(null);
	const [editFacilityFees, setEditFacilityFees] = React.useState<TermSheetFacility | null>(null);
	const [painPoints, setPainPoints] = React.useState<TermSheetFacility | null>(null);
	const [editFacilityCollateral, setEditFacilityCollateral] = React.useState<TermSheetFacility | null>(null);

	const theme = useTheme();
	const dataRoomQuery = useDataRoomQuery(dataRoomGuid);
	const dealQuery = useDealQuery(dataRoomQuery.data?.dealGuid, {
		enabled: dataRoomQuery.isSuccess,
		retry: false,
	});
	const showPainPoints =
		dealQuery.isSuccess &&
		dealQuery.data.dealStatus == 'ClosedFunded' &&
		hasFundingLender(dealQuery.data, termSheet.bankContactGuid);

	const closeDrawersAndDialogs = () => {
		setFacilityBeingAdded(false);
		setFacilityBeingDeleted(null);
		setFacilityBeingEdited(null);
		setEditFacilityFees(null);
		setEditFacilityCollateral(null);
		setPainPoints(null);
	};

	if (isLoading) {
		return (
			<>
				<Skeleton height={60} />
				<Skeleton height={60} />
				<Skeleton height={60} />
			</>
		);
	}

	return (
		<>
			<CerebroSecondaryButton onClick={() => setFacilityBeingAdded(true)} sx={{ marginBottom: 5 }}>
				Add
			</CerebroSecondaryButton>

			<Grid container spacing={2} alignItems="stretch">
				{revision.facilities.map(facility => {
					const isLease = isLeaseFacilityType(facility.facilityType);
					const factorPo = isFactorPO(facility.facilityType);
					const showBase = !isLease && !factorPo;

					return (
						<Grid key={revision.guid} item xs={12}>
							<Card
								key={revision.guid}
								elevation={3}
								sx={{
									display: 'flex',
									justifyContent: 'space-between',
									flexDirection: 'column',
									height: '100%',
									marginBottom: 2,
								}}
							>
								<CardContent sx={{ paddingBottom: '0 !important' }}>
									<Grid container sx={{ marginBottom: 2 }}>
										<Grid item xs={12} sx={{ display: 'flex' }}>
											<CerebroText variant="large" component="span" sx={{ fontWeight: 'bold' }}>
												{NumberUtils.FormatCurrency(facility.highEndAmount)}{' '}
												{EnumUtils.GetFacilityTypeDescription(facility.facilityType)}
											</CerebroText>
										</Grid>
									</Grid>
									<Grid container sx={{ marginTop: 2 }}>
										<Grid item xs={1}></Grid>
										<Grid item xs={3}>
											{facility.isAccordion &&
												facility.accordionAmount &&
												facility.accordionAmount > 0 && (
													<CerebroText>
														Accordion Amount:{' '}
														{NumberUtils.FormatCurrency(facility.accordionAmount)}
													</CerebroText>
												)}
											<CerebroText>Term: {facility.termMonths} months</CerebroText>
											{showBase && (
												<CerebroText>
													Interest Rate: {facility.interestCash}
													{EnumUtils.GetRateTypesDescription(facility.rateType)}{' '}
													{facility.interestRateType == 'Fixed'
														? EnumUtils.GetInterestRateTypesDescription(
																facility.interestRateType
														  )
														: ''}{' '}
													{facility.interestRateType == 'Float' && (
														<>
															{' '}
															+{' '}
															{facility.baseRate != 'Other'
																? EnumUtils.GetBaseRateTypesDescription(
																		facility.baseRate
																  )
																: facility.otherBaseRateName
																? facility.otherBaseRateName
																: 'Other'}{' '}
															{facility.baseRate == 'Other' &&
																facility.otherBaseRateValue && (
																	<>{facility.otherBaseRateValue}%</>
																)}
														</>
													)}
												</CerebroText>
											)}
											{isLease && facility.facilityFactorType == 'PaymentAmount' && (
												<CerebroText>
													Payment Amount:{' '}
													{NumberUtils.FormatCurrency(facility.leasePaymentAmount)}
												</CerebroText>
											)}
											{isLease && facility.facilityFactorType == 'RateFactor' && (
												<CerebroText>
													Rate Factor: {(facility.leaseRateFactor ?? 0).toFixed(4)}
												</CerebroText>
											)}
											{facility.baseRate == 'Other' && <></>}
										</Grid>
										<Grid item xs={4}>
											<CerebroText sx={{ fontWeight: 'bold' }}>Fees</CerebroText>
											{facility.fees
												.filter(fee => fee.termSheetFee != null)
												.map(fee => {
													return (
														<CerebroText key={fee.guid}>
															{fee.termSheetFee.feeName}{' '}
															{fee.termSheetFeeType == 'Currency' ? (
																<>{NumberUtils.FormatCurrency(fee.feeAmount)}</>
															) : (
																<>{fee.feeAmount}%</>
															)}{' '}
															{EnumUtils.GetTermSheetFeeFrequenciesDescription(
																fee.feeFrequency
															)}
														</CerebroText>
													);
												})}
											{facility.feesProvided && facility.fees.length == 0 && (
												<CerebroText sx={{ color: theme.palette.warning.main }}>
													Missing Information
												</CerebroText>
											)}
											{!facility.feesProvided && <CerebroText>Fees Not Provided</CerebroText>}
										</Grid>
										<Grid item xs={3}>
											<CerebroText sx={{ fontWeight: 'bold' }}>Collateral</CerebroText>
											{facility.collateral.map(col => {
												return (
													<CerebroText key={col.guid}>
														{EnumUtils.GetCollateralSecurityTypeDescription(
															col.collateralSecurityType
														)}
													</CerebroText>
												);
											})}
											{facility.collateral.length == 0 && (
												<CerebroText sx={{ color: theme.palette.warning.main }}>
													Missing Information
												</CerebroText>
											)}
										</Grid>
										<Grid item xs={1}></Grid>
									</Grid>
								</CardContent>
								<CardActions
									sx={theme => ({
										alignItems: 'flex-end',
										backgroundColor: theme.palette.gray.background,
										borderTop: `1px solid ${theme.palette.divider}`,
										justifyContent: 'right',
									})}
								>
									<TermSheetFacilityActionButton
										facility={facility}
										showPainPoints={showPainPoints}
										onEdit={setFacilityBeingEdited}
										onFees={setEditFacilityFees}
										onCollateral={setEditFacilityCollateral}
										onCreateDealSonar={setPainPoints}
										onDelete={setFacilityBeingDeleted}
									/>
								</CardActions>
							</Card>
						</Grid>
					);
				})}
				{revision.facilities.length == 0 && (
					<CerebroText>No facilities have been added to the term sheet.</CerebroText>
				)}
			</Grid>

			<AddFacilityDrawer
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				termSheet={termSheet}
				isOpen={facilityBeingAdded}
				close={closeDrawersAndDialogs}
			/>
			<EditFacilityDrawer
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				facility={facilityBeingEdited}
				close={closeDrawersAndDialogs}
			/>
			<FacilityDeleteDialog
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				facility={facilityBeingDeleted}
				close={closeDrawersAndDialogs}
			/>
			<FacilityFeeDrawer
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				facility={editFacilityFees}
				close={closeDrawersAndDialogs}
			/>
			<FacilityCollateralDrawer
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				facility={editFacilityCollateral}
				close={closeDrawersAndDialogs}
			/>
			<PainPointConfirmationDialog
				dataRoomGuid={dataRoomGuid}
				termSheetGuid={termSheetGuid}
				revisionGuid={revisionGuid}
				facility={painPoints}
				close={closeDrawersAndDialogs}
			/>
		</>
	);
};

function hasFundingLender(deal: Deal, bankContactGuid: string): boolean {
	return deal.fundingLenders.some(lender => lender.bankContactGuid === bankContactGuid);
}
